import { useState } from "react"
import { useFirestore, useFirestoreCollectionData } from "reactfire"
import { collection, doc, deleteDoc, setDoc } from "firebase/firestore"
import { useActividad } from "../hooks/useActividad"
import { ETAPAS_POR_UNIDAD, BADGE_ETAPA } from "../config/etapas"
import { UNIDADES, UNIDADES_CON_ROL, UNIDADES_SCOUT } from "../config/unidades"

const FICHA_CONFIG = {
  actualizada:    { label: "Actualizada",    clase: "badge-pagado"   },
  desactualizada: { label: "Desactualizada", clase: "badge-atrasado" },
}

export default function ApoderadosList({ unidadId, puedeEditar, onEditar }) {
  const firestore = useFirestore()
  const { data: apoderados, status } = useFirestoreCollectionData(
    collection(firestore, "unidades", unidadId, "apoderados"), { idField: "id" }
  )
  const { data: archivados, status: statusArchivados } = useFirestoreCollectionData(
    collection(firestore, "unidades", unidadId, "archivados"), { idField: "id" }
  )

  const [busqueda,        setBusqueda]       = useState("")
  const [orden,           setOrden]          = useState("nombre")
  const [tab,             setTab]            = useState("activos") // "activos" | "archivados"

  const { registrar } = useActividad()
  const esConsejo = UNIDADES_CON_ROL.includes(unidadId)
  const etapas    = ETAPAS_POR_UNIDAD[unidadId] || []

  // ── Eliminar → archivar ──────────────────────────────────────────────────
  const eliminar = async (ap) => {
    if (!window.confirm(`¿Archivar a "${ap.nombre}"?\nSus datos quedarán guardados en el historial.`)) return

    // 1. Guardar copia en "archivados" con fecha de eliminación
    const { id, ...datos } = ap
    await setDoc(doc(firestore, "unidades", unidadId, "archivados", id), {
      ...datos,
      archivadoEn: { seconds: Math.floor(Date.now() / 1000) },
    })

    // 2. Eliminar de "apoderados"
    await deleteDoc(doc(firestore, "unidades", unidadId, "apoderados", id))
    await registrar({ accion: "archivó", entidad: "apoderado", nombre: ap.nombre, unidadId })
  }

  // ── Restaurar desde archivados ───────────────────────────────────────────
  const restaurar = async (ap) => {
    if (!window.confirm(`¿Restaurar a "${ap.nombre}" como apoderado activo?`)) return
    const { id, archivadoEn, ...datos } = ap
    await setDoc(doc(firestore, "unidades", unidadId, "apoderados", id), datos)
    await deleteDoc(doc(firestore, "unidades", unidadId, "archivados", id))
    await registrar({ accion: "restauró", entidad: "apoderado", nombre: ap.nombre, unidadId })
  }

  if (status === "loading") return <div className="loading">Cargando...</div>

  // ── Filtrar y ordenar activos ────────────────────────────────────────────
  const q = busqueda.trim().toLowerCase()
  const filtrados = q
    ? (apoderados || []).filter(ap =>
        ap.nombre?.toLowerCase().includes(q) ||
        ap.scouts?.some(s => s.toLowerCase().includes(q))
      )
    : [...(apoderados || [])]

  const ORDEN_UNIDADES = ["bandada", "manada", "compania", "tropa", "avanzada", "clan"]

  const ordenados = filtrados.sort((a, b) => {
    if (esConsejo) {
      const posA = ORDEN_UNIDADES.indexOf(a.unidadRol)
      const posB = ORDEN_UNIDADES.indexOf(b.unidadRol)
      const iA = posA === -1 ? 999 : posA
      const iB = posB === -1 ? 999 : posB
      if (iA !== iB) return iA - iB
      return (a.nombre || "").localeCompare(b.nombre || "", "es")
    }
    if (orden === "nombre") return (a.nombre || "").localeCompare(b.nombre || "", "es")
    if (orden === "etapa") {
      const iA = etapas.findIndex(e => e.value === a.etapa)
      const iB = etapas.findIndex(e => e.value === b.etapa)
      return (iA === -1 ? 999 : iA) - (iB === -1 ? 999 : iB)
    }
    return 0
  })

  const grupos = orden === "etapa" && !esConsejo
    ? etapas.map(et => ({
        etapa: et,
        items: ordenados.filter(ap => ap.etapa === et.value),
      })).filter(g => g.items.length > 0).concat(
        ordenados.filter(ap => !ap.etapa).length > 0
          ? [{ etapa: { value: "__sin__", label: "Sin etapa asignada" }, items: ordenados.filter(ap => !ap.etapa) }]
          : []
      )
    : null

  const cantArchivados = (archivados || []).length

  return (
    <div>
      {/* ── Tabs activos / archivados ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setTab("activos")}
          style={{
            padding: "8px 18px", borderRadius: 10, border: "none", cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
            background: tab === "activos" ? "var(--verde)" : "var(--verde-suave)",
            color:      tab === "activos" ? "#fff"        : "var(--verde)",
            transition: "all 0.15s",
          }}
        >
          {esConsejo ? "🏛️ Miembros activos" : "👥 Apoderados activos"}
        </button>
        <button
          onClick={() => setTab("archivados")}
          style={{
            padding: "8px 18px", borderRadius: 10, border: "none", cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
            background: tab === "archivados" ? "var(--gris)" : "var(--verde-suave)",
            color:      tab === "archivados" ? "#fff"        : "var(--verde)",
            transition: "all 0.15s",
            position: "relative",
          }}
        >
          🗂️ Historial
          {cantArchivados > 0 && (
            <span style={{
              marginLeft: 8, background: "#6b7280", color: "#fff",
              borderRadius: 999, fontSize: 11, fontWeight: 700,
              padding: "1px 7px",
            }}>
              {cantArchivados}
            </span>
          )}
        </button>
      </div>

      {/* ════════════════════════════════════════
          TAB ACTIVOS
      ════════════════════════════════════════ */}
      {tab === "activos" && (
        <>
          {/* Barra de búsqueda y filtros */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <span style={{
                position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                fontSize: 16, pointerEvents: "none", opacity: 0.4,
              }}>🔍</span>
              <input
                className="form-input"
                style={{ paddingLeft: 40, marginBottom: 0 }}
                placeholder={esConsejo ? "Buscar por nombre o cargo..." : "Buscar apoderado o scout..."}
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda("")}
                  style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 16, color: "var(--gris)", padding: 4,
                  }}
                >✕</button>
              )}
            </div>
            {!esConsejo && (
              <div style={{ display: "flex", gap: 6 }}>
                {["nombre", "etapa"].map(o => (
                  <button
                    key={o}
                    onClick={() => setOrden(o)}
                    style={{
                      padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                      background: orden === o ? "var(--verde)" : "var(--verde-suave)",
                      color:      orden === o ? "#fff"        : "var(--verde)",
                      transition: "all 0.15s",
                    }}
                  >
                    {o === "nombre" ? "A→Z" : "Por etapa"}
                  </button>
                ))}
              </div>
            )}
          </div>

          {q && (
            <div style={{ fontSize: 13, color: "var(--gris)", marginBottom: 10 }}>
              {filtrados.length === 0
                ? "Sin resultados para ese término"
                : `${filtrados.length} resultado${filtrados.length !== 1 ? "s" : ""} para "${busqueda}"`
              }
            </div>
          )}

          {filtrados.length === 0 && q ? (
            <div className="empty-state" style={{ padding: "40px 20px" }}>
              <div className="empty-icon">🔍</div>
              <div className="empty-text">No se encontró ningún resultado.<br /><span style={{ fontSize: 13 }}>Intenta con otro nombre.</span></div>
            </div>
          ) : !apoderados || apoderados.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{esConsejo ? "🏛️" : "👥"}</div>
              <div className="empty-text">
                {esConsejo ? "No hay miembros en el Consejo." : "No hay apoderados en esta unidad."}
                {puedeEditar && <><br />Agrega el primero con el botón de arriba.</>}
              </div>
            </div>
          ) : grupos ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {grupos.map(({ etapa, items }) => (
                <div key={etapa.value}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    {etapa.value !== "__sin__" && BADGE_ETAPA[etapa.value]
                      ? <span className={`badge ${BADGE_ETAPA[etapa.value]}`}>{etapa.label}</span>
                      : <span className="badge" style={{ background: "var(--gris-borde)", color: "var(--gris)" }}>{etapa.label}</span>
                    }
                    <span style={{ fontSize: 13, color: "var(--gris)" }}>{items.length} apoderado{items.length !== 1 ? "s" : ""}</span>
                  </div>
                  <TablaApoderados items={items} esConsejo={esConsejo} puedeEditar={puedeEditar}
                    etapas={etapas} q={q}
                    onEditar={onEditar} eliminar={eliminar} />
                </div>
              ))}
            </div>
          ) : (
            <TablaApoderados items={ordenados} esConsejo={esConsejo} puedeEditar={puedeEditar}
              etapas={etapas} q={q}
              onEditar={onEditar} eliminar={eliminar} />
          )}
        </>
      )}

      {/* ════════════════════════════════════════
          TAB HISTORIAL / ARCHIVADOS
      ════════════════════════════════════════ */}
      {tab === "archivados" && (
        <div>
          {/* Banner informativo */}
          <div style={{
            background: "#f9fafb", border: "1px solid var(--gris-borde)",
            borderRadius: 12, padding: "14px 18px", marginBottom: 20,
            fontSize: 13, color: "var(--gris)", display: "flex", gap: 12, alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 20 }}>🗂️</span>
            <div>
              Aquí aparecen los {esConsejo ? "miembros" : "apoderados"} que fueron eliminados de la unidad.
              Sus datos quedan guardados como registro histórico.
              Puedes <strong>restaurarlos</strong> si fue un error o si vuelven a participar.
            </div>
          </div>

          {statusArchivados === "loading" ? (
            <div className="loading">Cargando historial...</div>
          ) : !archivados || archivados.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🗂️</div>
              <div className="empty-text">No hay registros en el historial aún.</div>
            </div>
          ) : (
            <div className="tabla-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 48 }}></th>
                    <th>{esConsejo ? "Dirigente / Guiadora" : "Apoderado"}</th>
                    <th>{esConsejo ? "Cargo" : "Scouts"}</th>
                    <th>{esConsejo ? "Unidad" : "Etapa"}</th>
                    <th>Archivado el</th>
                    {puedeEditar && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...(archivados || [])].sort((a, b) =>
                    (b.archivadoEn?.seconds || 0) - (a.archivadoEn?.seconds || 0)
                  ).map(ap => {
                    const etapaLabel  = etapas.find(e => e.value === ap.etapa)?.label
                    const unidadLabel = UNIDADES_SCOUT.find(u => u.value === ap.unidadRol)?.label
                    const fecha       = ap.archivadoEn
                      ? new Date(ap.archivadoEn.seconds * 1000).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" })
                      : "—"

                    return (
                      <tr key={ap.id} style={{ opacity: 0.85 }}>
                        {/* Foto */}
                        <td style={{ padding: "8px 12px" }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: "50%",
                            background: "var(--verde-suave)", overflow: "hidden",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 18, flexShrink: 0, filter: "grayscale(40%)",
                          }}>
                            {ap.fotoUrl
                              ? <img src={ap.fotoUrl} alt={ap.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <span>👤</span>
                            }
                          </div>
                        </td>

                        {/* Nombre */}
                        <td style={{ fontWeight: 600, color: "var(--texto-suave)" }}>
                          {ap.nombre}
                          {ap.descripcion && (
                            <div style={{ fontSize: 12, color: "var(--gris)", fontStyle: "italic", marginTop: 2 }}>
                              💬 {ap.descripcion}
                            </div>
                          )}
                        </td>

                        {/* Scouts */}
                        <td style={{ fontSize: 13, color: "var(--gris)" }}>
                          {ap.scouts?.join(", ") || "—"}
                        </td>

                        {/* Etapa / Unidad */}
                        <td>
                          {esConsejo
                            ? unidadLabel && ap.unidadRol && UNIDADES[ap.unidadRol]
                              ? <span className="badge" style={{ background: UNIDADES[ap.unidadRol].bg, color: UNIDADES[ap.unidadRol].texto, opacity: 0.8 }}>
                                  {UNIDADES[ap.unidadRol].icono} {unidadLabel}
                                </span>
                              : <span style={{ color: "var(--gris)", fontSize: 13 }}>—</span>
                            : ap.etapa && BADGE_ETAPA[ap.etapa]
                              ? <span className={`badge ${BADGE_ETAPA[ap.etapa]}`} style={{ opacity: 0.8 }}>{etapaLabel || ap.etapa}</span>
                              : <span style={{ color: "var(--gris)", fontSize: 13 }}>—</span>
                          }
                        </td>

                        {/* Fecha de archivado */}
                        <td style={{ fontSize: 13, color: "var(--gris)", whiteSpace: "nowrap" }}>
                          📅 {fecha}
                        </td>

                        {/* Acciones */}
                        {puedeEditar && (
                          <td>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                className="btn-icon"
                                title="Restaurar apoderado"
                                onClick={() => restaurar(ap)}
                                style={{ fontSize: 16 }}
                              >
                                ♻️
                              </button>
                              <button
                                className="btn-icon"
                                title="Eliminar permanentemente"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar permanentemente a "${ap.nombre}"?\nEsta acción no se puede deshacer.`)) return
                                  await deleteDoc(doc(firestore, "unidades", unidadId, "archivados", ap.id))
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Tabla de apoderados activos ── */
function TablaApoderados({ items, esConsejo, puedeEditar, etapas, q, onEditar, eliminar }) {
  return (
    <div className="tabla-wrapper">
      <table>
        <thead>
          <tr>
            <th style={{ width: 48 }}></th>
            <th>{esConsejo ? "Dirigente / Guiadora" : "Apoderado"}</th>
            <th>{esConsejo ? "Cargo" : "Scouts"}</th>
            <th>{esConsejo ? "Unidad" : "Etapa"}</th>
            <th>Inscrito</th>
            <th>Ficha Médica</th>
            {puedeEditar && <th>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((ap) => {
            const ficha       = FICHA_CONFIG[ap.fichaMedica]
            const etapaLabel  = etapas.find(e => e.value === ap.etapa)?.label
            const unidadLabel = UNIDADES_SCOUT.find(u => u.value === ap.unidadRol)?.label
            return (
              <>
                <tr
                  key={ap.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => onEditar(ap)}
                >
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "var(--verde-suave)", overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 18, flexShrink: 0,
                    }}>
                      {ap.fotoUrl
                        ? <img src={ap.fotoUrl} alt={ap.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <span>👤</span>
                      }
                    </div>
                  </td>

                  <td className="td-nombre">
                    {ap.exento && <span className="badge badge-exento" style={{ marginRight: 8 }}>No paga</span>}
                    <Resaltado texto={ap.nombre} busqueda={q} />

                  </td>

                  <td className="td-scouts">
                    {ap.scouts?.length
                      ? ap.scouts.map((s, i) => (<span key={i}>{i > 0 && ", "}<Resaltado texto={s} busqueda={q} /></span>))
                      : "—"
                    }
                  </td>

                  <td>
                    {esConsejo
                      ? unidadLabel && ap.unidadRol && UNIDADES[ap.unidadRol]
                        ? <span className="badge" style={{ background: UNIDADES[ap.unidadRol].bg, color: UNIDADES[ap.unidadRol].texto }}>{UNIDADES[ap.unidadRol].icono} {unidadLabel}</span>
                        : <span style={{ color: "var(--gris)", fontSize: 13 }}>—</span>
                      : ap.etapa && BADGE_ETAPA[ap.etapa]
                        ? <span className={`badge ${BADGE_ETAPA[ap.etapa]}`}>{etapaLabel || ap.etapa}</span>
                        : <span style={{ color: "var(--gris)", fontSize: 13 }}>—</span>
                    }
                  </td>

                  <td>
                    {ap.inscrito === false
                      ? <span className="badge badge-atrasado">✕ No</span>
                      : <span className="badge badge-pagado">✓ Sí</span>
                    }
                  </td>

                  <td>
                    {ficha
                      ? <span className={`badge ${ficha.clase}`}>{ficha.label}</span>
                      : <span style={{ color: "var(--gris)", fontSize: 13 }}>—</span>
                    }
                  </td>

                  {puedeEditar && (
                    <td onClick={e => e.stopPropagation()}>
                      <div className="tabla-acciones">
                        <button className="btn-icon" title="Archivar" onClick={e => { e.stopPropagation(); eliminar(ap) }}>🗑️</button>
                      </div>
                    </td>
                  )}
                </tr>


              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Resaltado({ texto, busqueda }) {
  if (!busqueda || !texto) return <>{texto}</>
  const idx = texto.toLowerCase().indexOf(busqueda.toLowerCase())
  if (idx === -1) return <>{texto}</>
  return (
    <>
      {texto.slice(0, idx)}
      <mark style={{ background: "#fef08a", color: "inherit", borderRadius: 3, padding: "0 2px" }}>
        {texto.slice(idx, idx + busqueda.length)}
      </mark>
      {texto.slice(idx + busqueda.length)}
    </>
  )
}
